import { useEffect, useRef, useState } from 'react';
import { useInViewport } from '../utils/useInViewport';
import { useSession } from '../utils/session';
import './LazyVideo.css';

const prefersStill = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData === true;

// blur placeholder → poster → looping video. The <video> only exists while the card is near the
// viewport (mobile browsers cap concurrent decoders) and fades in once it is actually playing.
// `priority`: the poster is fetched eagerly (it is the page's LCP). Unlike `eager` it does not keep a decoder alive.
export function LazyVideo({ src, poster, blur, muted = true, focus, alt = '', eager = false, priority = false }) {
    const wrapRef = useRef(null);
    const videoRef = useRef(null);
    const inView = useInViewport(wrapRef, '200px');
    const { ageConfirmed } = useSession();
    const [playing, setPlaying] = useState(false);
    const [still] = useState(prefersStill);

    const mount = ageConfirmed && !still && (inView || eager);

    useEffect(() => { if (!mount) setPlaying(false); }, [mount]);

    // React only sets `muted` on mount reliably; keep the element in sync afterwards.
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = muted;
        if (!muted && v.paused) v.play().catch(() => {});
    }, [muted, mount]);

    const style = {
        ...(blur ? { backgroundImage: `url(${blur})` } : null),
        ...(focus ? { '--focus': focus } : null),
    };

    return (
        <span className="lazyVideo" ref={wrapRef} style={style}>
            <img
                className="lazyVideoLayer"
                src={poster}
                alt={alt}
                loading={eager || priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : undefined}
                decoding="async"
            />
            {mount && (
                <video
                    ref={videoRef}
                    className={`lazyVideoLayer lazyVideoClip${playing ? ' isPlaying' : ''}`}
                    src={src}
                    autoPlay
                    muted={muted}
                    loop
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    onPlaying={() => setPlaying(true)}
                />
            )}
        </span>
    );
}
